import { useRef } from "react";
import { Camera, Loader2, Save, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { UserAvatar } from "@/components/common/UserAvatar";
import { cn } from "@/lib/utils";
import { slateCardClass, slateSurfaceClass } from "@/lib/card-styles";
import { typeBodyClass, typeMetaClass, typeTitleClass } from "@/lib/typography";

export interface ProfileInfoSectionProps {
  userId: string | null;
  email: string;
  displayName: string;
  setDisplayName: (value: string) => void;
  bio: string;
  setBio: (value: string) => void;
  previewUrl: string | null;
  avatarUrl: string;
  uploading: boolean;
  isUpdating: boolean;
  onImageUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveImage: () => void;
  onSaveProfile: () => void;
}

/**
 * Name, bio, and picture — the pane that answers "how do I appear to others".
 *
 * ## The avatar block is a card, not a row of loose buttons
 *
 * The picture, its two actions, and the file-type note were previously one
 * flex row of a centre-aligned avatar with two outline/ghost buttons floating
 * beside it. Now the whole thing is a `slateCardClass` sub-surface inside the
 * pane, which is where the design system puts a group of related controls.
 *
 * ## Save is the pane's one primary action
 *
 * Calm focus allows one primary CTA per screen. `heroCtaClass` is flat
 * `bg-primary` on purpose — the gradient ramp belongs to `primaryCtaClass` on a
 * full-width surface, not to the single button at the end of a form.
 */
export const ProfileInfoSection = ({
  userId,
  email,
  displayName,
  setDisplayName,
  bio,
  setBio,
  previewUrl,
  avatarUrl,
  uploading,
  isUpdating,
  onImageUpload,
  onRemoveImage,
  onSaveProfile,
}: ProfileInfoSectionProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const openPicker = () => fileInputRef.current?.click();

  return (
    <div className="space-y-6">
      <div>
        <h3 className={typeTitleClass}>Profile</h3>
        <p className={cn(typeBodyClass, "mt-1")}>
          Your display name and picture appear on everything you publish.
        </p>
      </div>

      <div className={cn(slateCardClass, "flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:gap-5 sm:p-5")}>
        <div className="relative shrink-0 self-center sm:self-auto">
          <button
            type="button"
            onClick={openPicker}
            disabled={uploading}
            className={cn(
              "group relative block rounded-full outline-none",
              "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
              uploading && "cursor-wait opacity-70",
            )}
            aria-label="Change profile picture"
          >
            <UserAvatar
              userId={userId}
              displayName={displayName}
              email={email}
              avatarUrl={previewUrl || avatarUrl}
              size="xl"
              className="h-20 w-20 shadow-sm"
              fallbackClassName="bg-primary-strong text-white font-semibold text-2xl"
            />
            <span className="absolute inset-0 flex items-center justify-center rounded-full bg-stone-900/50 opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100">
              {uploading ? (
                <Loader2 className="h-5 w-5 animate-spin text-white" aria-hidden="true" />
              ) : (
                <Camera className="h-5 w-5 text-white" aria-hidden="true" />
              )}
            </span>
          </button>
          <input
            ref={fileInputRef}
            id="avatar-upload"
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            onChange={onImageUpload}
            className="hidden"
            disabled={uploading}
          />
        </div>

        <div className="min-w-0 flex-1 text-center sm:text-left">
          <p className={typeMetaClass}>Profile picture</p>
          <p className={cn(typeBodyClass, "mt-1")}>JPEG, PNG, GIF, or WebP. Max 5MB.</p>
          <div className="mt-3 flex flex-wrap justify-center gap-2 sm:justify-start">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={uploading}
              onClick={openPicker}
              className="h-9 gap-1.5 rounded-lg text-xs"
            >
              {uploading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              ) : (
                <Camera className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              {uploading ? "Uploading…" : "Change photo"}
            </Button>
            {previewUrl && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onRemoveImage}
                disabled={uploading}
                className="h-9 gap-1.5 rounded-lg text-xs text-stone-500 hover:text-destructive dark:text-stone-400"
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                Remove
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="display-name" className={typeMetaClass}>
            Display name
          </Label>
          <Input
            id="display-name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Enter your display name"
            className="h-10 rounded-lg"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="bio" className={typeMetaClass}>
            Bio
          </Label>
          <Textarea
            id="bio"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder="Tell us about yourself..."
            rows={3}
            className="resize-none rounded-lg"
          />
        </div>
      </div>

      {/* Sticky so the primary action stays reachable once a long bio has been
          scrolled past — the form is the pane's job and Save is its one CTA. */}
      <div className={cn(slateSurfaceClass, "flex items-center justify-between gap-3 px-3 py-2.5 sm:sticky sm:bottom-0")}>
        <p className={cn("text-xs text-stone-500 dark:text-stone-400")}>
          Changes apply to your public profile.
        </p>
        <Button
          onClick={onSaveProfile}
          disabled={isUpdating}
          className="h-9 shrink-0 gap-1.5 rounded-lg px-4 text-xs font-semibold shadow-sm"
        >
          {isUpdating ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          ) : (
            <Save className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          {isUpdating ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
};

export default ProfileInfoSection;